import { getImageUrl } from '@/lib/media';
import { PurchasedPackage } from '../../../../services/student/packages.interfaces';
import { cn } from '@/lib/utils';

interface PurchasedPackageCardProps {
  pkg: PurchasedPackage;
  onClick?: (event: React.MouseEvent<HTMLDivElement>) => void;
  onRenew?: () => void;
  highlighted?: boolean;
}

function isCompletedProgress(pkg: PurchasedPackage): boolean {
  const progress = pkg.studentProgress;
  if (!progress) return false;
  const status = progress.status?.toLowerCase();
  return (
    progress.isCompleted === true ||
    progress.progress >= 100 ||
    status === "completed" ||
    status === "concluido"
  );
}

function progressPercent(pkg: PurchasedPackage): number {
  const raw = pkg.studentProgress?.progress;
  if (typeof raw !== "number" || Number.isNaN(raw)) return 0;
  return Math.min(100, Math.max(0, raw));
}

export const PurchasedPackageCard = ({
  pkg,
  onClick,
  onRenew,
  highlighted = false,
}: PurchasedPackageCardProps) => {
  const isExpired = pkg.isValidNow === false;
  const percent = progressPercent(pkg);
  const renderStatus = () => {
    if (isCompletedProgress(pkg)) {
      return (
        <div className="flex items-center gap-2 absolute bottom-8 left-8">
          <div className="w-6 h-6 bg-white rounded-full flex items-center justify-center">
            <svg className="w-4 h-4" viewBox="0 0 24 24" fill="none">
              <path d="M5 13l4 4L19 7" stroke="black" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" />
            </svg>
          </div>
          <span className="text-white font-medium">Concluído</span>
        </div>
      );
    }

    return (
      <div className="flex items-center gap-2 absolute bottom-8 left-8">
        <div className="relative w-6 h-6">
          <svg className="w-6 h-6" viewBox="0 0 24 24">
            <circle
              cx="12"
              cy="12"
              r="10"
              stroke="rgba(255,255,255,0.3)"
              strokeWidth="2"
              fill="none"
            />
            <circle
              cx="12"
              cy="12"
              r="10"
              stroke="#4CAF50"
              strokeWidth="2"
              fill="none"
              strokeDasharray="63"
              strokeDashoffset={63 - (63 * percent) / 100}
              strokeLinecap="round"
              transform="rotate(-90 12 12)"
            />
          </svg>
        </div>
        <span className="text-white font-medium">{percent}%</span>
      </div>
    );
  };

  return (
    <div
      data-package-id={pkg.id}
      onClick={onClick}
      className={cn(
        "min-w-[500px] h-[280px] rounded-2xl flex-shrink-0 relative overflow-hidden cursor-pointer group transition-shadow duration-300",
        highlighted && "ring-4 ring-success-subtle shadow-lg shadow-success-muted/40"
      )}
      style={{
        backgroundImage: `url(${getImageUrl(pkg.imageUrl || pkg.image)})`,
        backgroundSize: "cover",
        backgroundPosition: "center",
      }}
    >
      <div className="absolute inset-0 bg-gradient-to-b from-black/40 to-transparent group-hover:from-black/50 transition-all duration-300" />
      <div className="relative z-10 p-8">
        <h2 className="text-white text-xl font-medium">{pkg.name}</h2>
        {isExpired ? (
          <p className="mt-2 text-sm font-medium text-white/90">Licença vencida</p>
        ) : null}
      </div>
      {renderStatus()}
      {isExpired && onRenew ? (
        <div className="absolute bottom-8 right-8 z-20">
          <button
            type="button"
            onClick={(event) => {
              event.stopPropagation();
              onRenew();
            }}
            className="bg-success-subtle hover:bg-success-subtle/90 text-white text-sm font-medium px-5 py-2 min-h-[44px] rounded-md transition-colors duration-200"
          >
            Renovar
          </button>
        </div>
      ) : null}
    </div>
  );
};
